import { Keypoint, COCOAnnotation, BoundingBox, KeypointVisibility } from '../types'

/**
 * Convert keypoints array to COCO format
 * @param keypoints Array of Keypoint objects
 * @returns Flattened array [x1, y1, v1, x2, y2, v2, ...] where v is visibility
 */
export function keypointsToCOCOFormat(keypoints: Keypoint[]): number[] {
  const cocoKeypoints: number[] = []

  for (const kp of keypoints) {
    // COCO standard: unlabeled points should be x=0, y=0, v=0
    if (kp.x < 0 || kp.y < 0) {
      cocoKeypoints.push(0)
      cocoKeypoints.push(0)
      cocoKeypoints.push(0)
    } else {
      cocoKeypoints.push(kp.x)
      cocoKeypoints.push(kp.y)

      // Handle three-state visibility
      let visibility = 2 // default to visible
      switch (kp.visibility) {
        case 'not_labeled':
          visibility = 0
          break
        case 'inferred':
          visibility = 1
          break
        case 'visible':
          visibility = 2
          break
      }

      cocoKeypoints.push(visibility)
    }
  }

  return cocoKeypoints
}

/**
 * Convert COCO format keypoints to Keypoint array
 * @param cocoKeypoints Flattened array [x1, y1, v1, x2, y2, v2, ...]
 * @returns Array of Keypoint objects
 */
export function cocoFormatToKeypoints(cocoKeypoints: number[]): Keypoint[] {
  const keypoints: Keypoint[] = []

  for (let i = 0; i < cocoKeypoints.length; i += 3) {
    const x = cocoKeypoints[i]
    const y = cocoKeypoints[i + 1]
    const visibility = cocoKeypoints[i + 2]

    // Map COCO visibility to our three-state system
    let visibilityState: KeypointVisibility = 'visible'
    if (visibility === 0) {
      visibilityState = 'not_labeled'
    } else if (visibility === 1) {
      visibilityState = 'inferred'
    } else {
      visibilityState = 'visible'
    }

    keypoints.push({
      x: visibility === 0 ? -1 : x,
      y: visibility === 0 ? -1 : y,
      confidence: 1.0,
      visibility: visibilityState,
    })
  }

  return keypoints
}

/**
 * Count the number of labeled keypoints
 * @param cocoKeypoints Flattened array [x1, y1, v1, x2, y2, v2, ...]
 * @returns Number of keypoints with visibility > 0
 */
export function countLabeledKeypoints(cocoKeypoints: number[]): number {
  let count = 0
  for (let i = 2; i < cocoKeypoints.length; i += 3) {
    if (cocoKeypoints[i] > 0) {
      count++
    }
  }
  return count
}

/**
 * Create a new COCO annotation
 * @param bbox Bounding box [x, y, width, height]
 * @param keypoints Array of Keypoint objects
 * @param id Optional annotation ID
 * @returns COCOAnnotation object
 */
export function createCOCOAnnotation(
  bbox: [number, number, number, number],
  keypoints: Keypoint[],
  id?: string
): COCOAnnotation {
  const cocoKeypoints = keypointsToCOCOFormat(keypoints)

  return {
    id: id || generateAnnotationId(),
    category_id: 0, // Default to 'baby' category
    bbox,
    keypoints: cocoKeypoints,
    num_keypoints: countLabeledKeypoints(cocoKeypoints),
  }
}

/**
 * Generate a unique annotation ID
 */
export function generateAnnotationId(): string {
  return `ann_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Convert BoundingBox to COCO bbox format
 */
export function boundingBoxToCOCO(box: BoundingBox): [number, number, number, number] {
  return [box.x, box.y, box.width, box.height]
}

/**
 * Convert COCO bbox to BoundingBox
 */
export function cocoBboxToBoundingBox(bbox: [number, number, number, number]): BoundingBox {
  return {
    x: bbox[0],
    y: bbox[1],
    width: bbox[2],
    height: bbox[3],
  }
}

/**
 * Initialize empty keypoints for a given schema length
 */
export function initializeEmptyKeypoints(schemaLength: number): Keypoint[] {
  return Array(schemaLength)
    .fill(null)
    .map(() => ({
      x: -1,
      y: -1,
      confidence: 1.0,
      visibility: 'not_labeled' as KeypointVisibility,
    }))
}
